import { BaseLoadingStore } from "app/modules/common/stores/BaseLoadingStore";
import { action, makeObservable, observable, runInAction } from "mobx";
import { FieldState, FormState } from "formstate";
import { int, required, requiredEditor, requiredNumber } from "app/modules/common/form/validators";
import { inject } from "react-ioc";
import { ApiClients } from "app/modules/company/services/AxiosBaseClient";
import { MainStore } from "app/modules/company/stores/MainStore";
import { CarrierBidSettings, CarrierEditModel, CompanyIntegrationType, EmailBidSettingParam, NamedValueLong, TenantClaims } from "app/modules/company/api/clients.api";
import moment from "moment";
import { CarrierEmailEditModel } from "app/modules/company/api/clients.api";
import { ContentState, EditorState } from 'draft-js';

export class CarrierDetailsStore extends BaseLoadingStore {
    @inject companyApiClients: ApiClients;
    @inject mainStore: MainStore;

    title: string = 'New Carrier';

    inboxIntegrations: NamedValueLong[] = [];
    params: EmailBidSettingParam[] = [];

    defaultSubject: string = "[OrderNnumber] • [LocationCombined] • [Rate] • [MilesOut] miles out • (MC: [CompanyMC])";
    defaultBody: string = `Bid: <b>[Rate]</b>
[CustomMessage]

BOX DIMS LxWxH (inches): <b>[VehicleBoxDimsLWH]</b>
DOOR DIMS WxH (inches): <b>[VehicleDoorDimsWH]</b>
Max Load: <b>[VehicleMaxLoad]</b>

[VehicleSpecs]

TimeZone: <b>PST</b>

-------------------------------------------

<b>[DispatcherFullName] | Dispatch Team</b>
[DispatcherPhone] | [CompanyDispatchPhone]
[DispatcherEmail]
<b>[CompanyName]</b> | USDOT [CompanyUSDOT]| <b>MC [CompanyMC]</b>`;

    carrierId: number;
    isActive: boolean = true;
    formState = new FormState({
        displayName: new FieldState('').validators(required),
        name: new FieldState('').validators(required),
        dbaName: new FieldState(''),
        uSDOT: new FieldState(null).validators(requiredNumber, int),
        mcNumber: new FieldState(null).validators(requiredNumber, int),
        eIN: new FieldState(null).validators(requiredNumber, int),
        insuranceExpirationDate: new FieldState<moment.Moment>(null).validators(required),

        address: new FieldState("").validators(required),
        address2: new FieldState(""),
        city: new FieldState("").validators(required),
        state: new FieldState("").validators(required),
        zip: new FieldState(null).validators(required),
        phone: new FieldState("").validators(required),
        allowAutoBid: new FieldState(false),
        autoBidRate: new FieldState(null),
        bypassRateRangeValidation: new FieldState(false),
        carrierEmailEditModel: new FieldState({
            inboxIntegrationId: new FieldState<number>(null),
            bidSettings: new FieldState({
                subjectTemplate: new FieldState(this.defaultSubject).validators(required),
                bodyTemplate: new FieldState(EditorState.createWithContent(ContentState.createFromText(this.defaultBody))).validators(requiredEditor),
            })
        }),
    });

    isReadOnlyMode: boolean = false;

    constructor() {
        super();

        this.isReadOnlyMode = !this.mainStore.checkClaim(TenantClaims.CarrierEdit);

        makeObservable(this, {
            title: observable,
            isActive: observable,
            params: observable,

            init: action,
            activate: action,
            deactivate: action,
            loadParams: action,
            save: action
        });
    }

    save = async () => {
        await this.formState.validate();

        if (this.formState.hasError) {
            return;
        }

        await this.wrapLoadingApiCall(async () => {
            await this.companyApiClients.carrierClient.upsert(new CarrierEditModel({
                id: this.carrierId,
                displayName: this.formState.$.displayName.value,
                name: this.formState.$.name.value,
                dbaName: this.formState.$.dbaName.value,
                uSDOT: this.formState.$.uSDOT.value,
                mC: this.formState.$.mcNumber.value?.toString() || '',
                eIN: this.formState.$.eIN.value,
                insuranceExpirationDate: this.formState.$.insuranceExpirationDate.value,

                address: this.formState.$.address.value,
                address2: this.formState.$.address2.value,
                city: this.formState.$.city.value,
                state: this.formState.$.state.value,
                zIP: this.formState.$.zip.value,

                phone: this.formState.$.phone.value,
                allowAutoBid: this.formState.$.allowAutoBid.value,
                autoBidRate: this.formState.$.autoBidRate.value,
                bypassRateRangeValidation: this.formState.$.bypassRateRangeValidation.value,
                carrierEmailEditModel: this.formState.$.carrierEmailEditModel.value.inboxIntegrationId.value > 0 ? new CarrierEmailEditModel({
                    inboxIntegrationId: this.formState.$.carrierEmailEditModel.value.inboxIntegrationId.value,
                    bidSettings: new CarrierBidSettings({
                        subjectTemplate: this.formState.$.carrierEmailEditModel.$.bidSettings.$.subjectTemplate.value,
                        bodyTemplate: this.formState.$.carrierEmailEditModel.$.bidSettings.$.bodyTemplate.value.getCurrentContent().getPlainText("\n"),
                    })
                }) : null
            }));

            runInAction(() => {
                this.isSavedSuccessfully = true;
            })
        });
    }

    activate = async () => {
        this.wrapLoadingApiCall(async () => {
            await this.companyApiClients.carrierClient.activate(this.carrierId);
            runInAction(() => {
                this.isActive = true;
            });
        });
    }

    deactivate = async () => {
        this.wrapLoadingApiCall(async () => {
            await this.companyApiClients.carrierClient.deactivate(this.carrierId);
            runInAction(() => {
                this.isActive = false;
            });
        });
    }

    replaceParams = (value: string) => {
        let val = value?.toString() || "";

        for (let i = 0; i < this.params.length; i++) {
            val = val.split(this.params[i].param).join(this.params[i].example);
        }

        return val;
    }

    loadParams = async () => {
        this.params = await this.companyApiClients.settingsClient.getCarrierEmailBidSettingParams(this.carrierId, this.formState.$.carrierEmailEditModel.value.inboxIntegrationId.value);
    }

    init = async (id: number) => {
        this.carrierId = id;

        await this.wrapInitLoadingApiCall(async () => {
            const [inboxIntegrations] = [
                await this.companyApiClients.companyIntegrationClient.getShortListByType(CompanyIntegrationType.Inbox),
            ];

            this.inboxIntegrations = inboxIntegrations;

            if (this.carrierId > 0) {
                const result = await this.companyApiClients.carrierClient.getById(this.carrierId);

                this.title = result.displayName;
                this.isActive = result.active;

                this.formState.$.displayName.value = result.displayName;
                this.formState.$.name.value = result.name;
                this.formState.$.dbaName.value = result.dbaName;
                this.formState.$.uSDOT.value = result.uSDOT;
                this.formState.$.mcNumber.value = result.mC;
                this.formState.$.eIN.value = result.eIN;

                this.formState.$.address.value = result.address;
                this.formState.$.address2.value = result.address2;
                this.formState.$.city.value = result.city;
                this.formState.$.state.value = result.state;
                this.formState.$.zip.value = result.zIP;
                this.formState.$.phone.value = result.phone;

                this.formState.$.insuranceExpirationDate.value = result.insuranceExpirationDate;

                this.formState.$.allowAutoBid.value = result.allowAutoBid;
                this.formState.$.autoBidRate.value = result.autoBidRate;
                this.formState.$.bypassRateRangeValidation.value = result.bypassRateRangeValidation;

                this.formState.$.carrierEmailEditModel.value.inboxIntegrationId.value = result.carrierEmailEditModel?.inboxIntegrationId;
                if (result.carrierEmailEditModel?.inboxIntegrationId > 0) {
                    this.formState.$.carrierEmailEditModel.value.bidSettings.$.subjectTemplate.value = result.carrierEmailEditModel?.bidSettings?.subjectTemplate || '';
                    this.formState.$.carrierEmailEditModel.value.bidSettings.$.bodyTemplate.value = EditorState.createWithContent(ContentState.createFromText(result.carrierEmailEditModel?.bidSettings?.bodyTemplate || ''));
                    this.params = await this.companyApiClients.settingsClient.getCarrierEmailBidSettingParams(this.carrierId, result.carrierEmailEditModel?.inboxIntegrationId);
                }
            }
        });
    }
}
